import { ProjectId } from "@custom-types/base-types";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { QueryParams } from "@router/route-params";
import {
  selectedAnalyticsProjectSelector,
  selectedAnalyticsTimeFrameSelector,
} from "@store/analytics/analytics-selector";
import {
  DEFAULT_TIME_FRAME,
  setSelectedAnalyticsProjectId,
  setSelectedAnalyticsTimeFrame,
} from "@store/analytics/analytics-slice";
import { fetchProjectStats } from "@store/analytics/analytics-slice-thunk";
import { projectIdsSelector } from "@store/projects/projects-selector";
import { fetchProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { AVAILABLE_TIME_FRAMES } from "@utils/time-utils";
import { useCallback, useEffect } from "react";
import { useLocation, useMatch } from "react-router-dom";

/** Specify base number to parse integers from */
const RADIX = 10;

/**
 * Hook that listens to changes in the URL and updates the selected project
 * and selected time frame for the analytics page.
 */
export function useAnalytics(): void {
  const location = useLocation();
  const { getQueryParam } = useAppNavigation();
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const selectedAnalyticsProject = useAppSelector(
    selectedAnalyticsProjectSelector
  );
  const selectedAnalyticsTimeFrame = useAppSelector(
    selectedAnalyticsTimeFrameSelector
  );

  const projectIds = useAppSelector(projectIdsSelector);

  /**
   * Updates a query parameter in the URL without pushing a new history entry.
   *
   * Normally, updating the URL directly (e.g. setUrlParam) creates a new history entry,
   * which causes issues when the user navigates "back" in the browser — they might have to go
   * through multiple "state-only" URL changes.
   *
   * This function solves that by using `history.replaceState`, which replaces the current history
   * entry instead of adding a new one. That ensures the browser back button works as expected,
   * without polluting the history stack with internal state changes.
   */
  const replaceUrlParam = useCallback(
    ({ key, value }: { key: string; value: string }) => {
      const url = new URL(window.location.href);
      url.searchParams.set(key, value);
      window.history.replaceState({}, "", url.toString());
    },
    []
  );

  const isWorkspaceRoute = useMatch(`/${companyId}/analytics/workspace`);

  // Listen to changes in the url and update the selected time frame
  useEffect(() => {
    if (!isWorkspaceRoute) {
      return;
    }

    const selectedTimeFrameQuery = getQueryParam(QueryParams.timeFrame);

    if (!selectedTimeFrameQuery && selectedAnalyticsTimeFrame) {
      replaceUrlParam({
        key: QueryParams.timeFrame,
        value: selectedAnalyticsTimeFrame.toString(),
      });
      // Early return because the url will change and this useEffect will be called again
      return;
    }

    // If there's no time frame in the url we don't need to set anything else
    if (!selectedTimeFrameQuery) {
      return;
    }

    const newSelectedTimeFrame = parseInt(selectedTimeFrameQuery, RADIX);
    // Makes sure the user can't set an invalid time frame
    if (!AVAILABLE_TIME_FRAMES.includes(newSelectedTimeFrame)) {
      replaceUrlParam({
        key: QueryParams.timeFrame,
        value: DEFAULT_TIME_FRAME.toString(),
      });
      // Early exit since changing the url will trigger this useEffect again
      return;
    }
    dispatch(setSelectedAnalyticsTimeFrame(newSelectedTimeFrame));
  }, [
    selectedAnalyticsTimeFrame,
    getQueryParam,
    replaceUrlParam,
    dispatch,
    isWorkspaceRoute,
  ]);

  // Listen to changes in the url and update the selected project
  useEffect(() => {
    /**
     * Resets the selected project to all projects.
     */
    function resetSelectedProject(): void {
      replaceUrlParam({
        key: QueryParams.projectId,
        value: "all",
      });
    }

    /**
     * Fetches the project details for the selected project if it's not already in the list of projects.
     * If fails to fetch the project details, resets the selected project to all projects.
     */
    async function fetchSelectedProjectDetails(
      projectId: ProjectId | null
    ): Promise<void> {
      // If we don't have the project in the list of projects, we need to fetch it,
      // otherwise we can't show the name in the selection.
      if (projectId && !projectIds.includes(projectId)) {
        try {
          await dispatch(
            fetchProjectDetails({
              companyId,
              projectId,
            })
          ).unwrap();
        } catch {
          resetSelectedProject();
          // Early exit since changing the url will trigger this useEffect again
          return;
        }
      }
    }

    /**
     * Fetches the project stats for the selected project.
     * If fails to fetch the project stats, resets the selected project to all projects.
     */
    async function fetchSelectedProjectStats(
      projectId: ProjectId | null
    ): Promise<void> {
      if (projectId) {
        try {
          await dispatch(
            fetchProjectStats({
              projectId,
              companyId,
            })
          ).unwrap();
        } catch {
          resetSelectedProject();
          // Early exit since changing the url will trigger this useEffect again
          return;
        }
      }
    }

    async function fetchProjectDetailsAndStats(): Promise<void> {
      const queryProjectId = getQueryParam(QueryParams.projectId);
      if (!queryProjectId && selectedAnalyticsProject) {
        replaceUrlParam({
          key: QueryParams.projectId,
          value: selectedAnalyticsProject.id ?? "all",
        });
        // Early return because the url will change and this useEffect will be called again
        return;
      }

      // If there's no project in the url we don't need to set or fetch anything else
      if (!queryProjectId) {
        return;
      }

      const projectId = queryProjectId === "all" ? null : queryProjectId;

      dispatch(setSelectedAnalyticsProjectId(projectId));

      // Since the project details are irrelevant for the project stats, we can fetch them in parallel.
      await Promise.all([
        fetchSelectedProjectDetails(projectId),
        fetchSelectedProjectStats(projectId),
      ]);
    }

    void fetchProjectDetailsAndStats();
  }, [
    location,
    companyId,
    projectIds,
    selectedAnalyticsProject,
    dispatch,
    replaceUrlParam,
    getQueryParam,
  ]);
}
